import ModalReportesCartera from './ModalReportesCartera';
import ModalDetallePO from './ModalDetallePO';
import React, { useState, useEffect, useMemo, useCallback, useRef, useLayoutEffect } from 'react';
import Select from 'react-select';
import { distribuirPorAntiguedad } from '../utils/pagoAntiguedad';
import api from '../services/api';
import ModalMovimiento from './ModalMovimiento';
import ModalEstadoCuenta from './ModalEstadoCuenta';
import ModalAplicarPrepagos from './ModalAplicarPrepagos';
import ModalNotaCredito from './ModalNotaCredito';
import ModalReportesMasterCartera from './ModalReportesMasterCartera';

const ETIQUETAS_TIPO_DOCUMENTO = {
  F: 'FACTURA',
  PG: 'PAGO',
  PP: 'PREPAGO ANTICIPO',
  NC: 'NOTA CREDITO',
  ND: 'NOTA DEBITO',
  RT: 'RETENCION',
  SI: 'SALDO INICIAL',
  SA: 'SALDO ANTERIOR'
};

const COLORES_TIPO_DOCUMENTO = {
  F: ['#dbeafe', '#1e40af', '#93c5fd'],
  SI: ['#fef3c7', '#92400e', '#fcd34d'],
  NC: ['#f3e8ff', '#6b21a8', '#d8b4fe'],
  PG: ['#dcfce7', '#166534', '#86efac'],
  PP: ['#ccfbf1', '#115e59', '#5eead4'],
  ND: ['#fee2e2', '#991b1b', '#fca5a5'],
  RT: ['#ffedd5', '#9a3412', '#fdba74'],
  SA: ['#f1f5f9', '#475569', '#cbd5e1']
};

function TipoDocumento({ tipo }) {
  const codigo = String(tipo || '').toUpperCase();
  const nombre = ETIQUETAS_TIPO_DOCUMENTO[codigo] || codigo;
  const [background, color, borde] = COLORES_TIPO_DOCUMENTO[codigo] || COLORES_TIPO_DOCUMENTO.SA;
  return <span title={`${codigo}: ${nombre}`} aria-label={`${codigo}: ${nombre}`} style={{
    display: 'inline-block', padding: '3px 6px', borderRadius: 5,
    background, color, border: `1px solid ${borde}`, fontWeight: 700,
    fontSize: 12, lineHeight: 1.2
  }}>{codigo}</span>;
}

const normalizarTextoBusqueda = (valor) =>
  String(valor ?? '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();

function CarteraPage() {
  const gridRef = useRef(null);
  useLayoutEffect(() => {
    const grid = gridRef.current;
    const ajustar = () => {
      grid.style.maxHeight = `${Math.max(200, window.innerHeight - grid.getBoundingClientRect().top - 16)}px`;
    };
    const observer = new ResizeObserver(ajustar);
    observer.observe(grid.parentElement);
    window.addEventListener('resize', ajustar);
    ajustar();
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', ajustar);
    };
  }, []);
  const [mostrarReportes,setMostrarReportes]=useState(false);
  const [facturaPO,setFacturaPO]=useState(null);
  const [tipoMovimiento, setTipoMovimiento] = useState('C');
  const [idTercero, setIdTercero] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [busquedaMovimientos, setBusquedaMovimientos] = useState('');

  const [timeline, setTimeline] = useState([]);
  const [facturasIndex, setFacturasIndex] = useState({});

  const [terceros, setTerceros] = useState([]);

  const opcionesTerceros = useMemo(
    () =>
      terceros.map((tercero) => ({
        value: String(tercero.idtercero),
        label: tercero.nombre
      })),
    [terceros]
  );

  const opcionTerceroSeleccionada = useMemo(
    () => opcionesTerceros.find((opcion) => opcion.value === String(idTercero)) || null,
    [opcionesTerceros, idTercero]
  );

  const [showModal, setShowModal] = useState(false);
  const [showPrepagos, setShowPrepagos] = useState(false);
  const [showNC, setShowNC] = useState(false);
  const [idpagoNCEditar, setIdpagoNCEditar] = useState(null);
  const [movimientoParaEditar, setMovimientoParaEditar] = useState(null);
  const [seleccionados, setSeleccionados] = useState({});
  const [relacionMarcada, setRelacionMarcada] = useState(null);
  const [mostrarModalEstadoCuenta, setMostrarModalEstadoCuenta] = useState(false);
  const [mostrarModalReportesMaster, setMostrarModalReportesMaster] = useState(false);
  const [terceroActual, setTerceroActual] = useState(null);
  const [rangoFechas, setRangoFechas] = useState({ desde: '', hasta: '' });

  // Panel de prepagos
  const [prepagos, setPrepagos] = useState([]);
  const [hayPrepagos, setHayPrepagos] = useState(false);

  // saldo inicial para arrancar el acumulado
  const [saldoInicial, setSaldoInicial] = useState(0);

  // ✅ filtro visual “Solo pendientes”
  const [soloPendientes, setSoloPendientes] = useState(false);

  // ✅ Formato moneda (USD) con símbolo $
  const moneyFmt = useMemo(
    () =>
      new Intl.NumberFormat('es-EC', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }),
    []
  );
  const fmt = (n) => moneyFmt.format(Number(n || 0));
  // Ocultar solo la representacion del cero, conservando los importes y calculos.
  const fmtColumna = (n) => Math.abs(Number(n || 0)) < 0.005 ? '' : fmt(n);

  const splitFacturaCliente = (markRaw) => {
    const s = String(markRaw ?? '').trim();
    const m = /^(\d{3,})\s*(?:[-–—]\s*)?(.*)$/.exec(s);
    if (m && m[2] && m[2].trim()) {
      return { facturaCliente: m[1], markLimpio: m[2].trim() };
    }
    return { facturaCliente: '', markLimpio: s };
  };

  // --- helpers ---
  const normClas = (v) =>
    String(v ?? '')
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase();

  const toNum = (v) => {
    if (v === null || v === undefined || v === '') return 0;
    if (typeof v === 'number') return v;
    const s = String(v).trim().replace(/\s+/g, '');
    const n = parseFloat(s.replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
  };
  const pickNum = (obj, keys) => {
    for (const k of keys) {
      if (k in obj) {
        const n = toNum(obj[k]);
        if (n !== 0 || obj[k] === 0) return n;
      }
    }
    return 0;
  };

  // 'DD/MM/YYYY' para UI, inmune a zonas horarias
  const dmy = useCallback((ymd) => {
    if (!ymd) return '';
    // ejemplo simple: "2026-01-05" -> "05/01/2026"
    const [y, m, d] = String(ymd).slice(0, 10).split('-');
    if (!y || !m || !d) return '';
    return `${d}/${m}/${y}`;
  }, []);

  // ✅ sumar/restar días sobre un 'YYYY-MM-DD' en UTC
  const addDaysYmd = (s, days) => {
    if (!s) return '';
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (!m) return '';
    const dt = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
    dt.setUTCDate(dt.getUTCDate() + days);
    const y = dt.getUTCFullYear();
    const mo = String(dt.getUTCMonth() + 1).padStart(2, '0');
    const d = String(dt.getUTCDate()).padStart(2, '0');
    return `${y}-${mo}-${d}`;
  };

  // ---- cargar terceros (solo CLIENTE cuando es Clientes) ----
  useEffect(() => {
    const tipo = tipoMovimiento === 'C' ? 'cliente' : 'proveedor';
    const params = tipo === 'cliente' ? { tipo, solo_principales: 1 } : { tipo };

    api
      .get('/api/terceros', { params })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : [];

        if (tipoMovimiento === 'C') {
          // ✅ Solo principales
          const principales = data
            .filter((t) => normClas(t.clasifcliente) === 'CLIENTE')
            .map((t) => ({
              idtercero: Number(t.idtercero),
              nombre: t.nombre
            }));

          setTerceros(principales);

          if (idTercero && !principales.some((p) => String(p.idtercero) === String(idTercero))) {
            setIdTercero('');
          }
        } else {
          const provs = data.map((t) => ({
            idtercero: Number(t.idtercero),
            nombre: t.nombre
          }));
          setTerceros(provs);
        }
      })
      .catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoMovimiento]);

  const buscar = async (opts = {}) => {
    try {
      const lado = String(tipoMovimiento || '')
        .toUpperCase()
        .trim()
        .startsWith('P')
        ? 'P'
        : 'C';

      const hoyYmd = new Date().toISOString().slice(0, 10);

      // ✅ permite llamar buscar con fechas directas, sin depender del setState
      const desdeBusqueda = (opts.desde ?? desde ?? '').slice(0, 10);
      const hastaBusqueda = (opts.hasta ?? hasta ?? hoyYmd).slice(0, 10);

      if (!idTercero) {
        alert('Selecciona un tercero.');
        return;
      }

      if (!desdeBusqueda || !hastaBusqueda) {
        alert('Selecciona desde / hasta.');
        return;
      }

      const hastaSaldo = hastaBusqueda || hoyYmd;

      // ---------- FACTURAS ----------
      const { data: facturas } = await api.get('/api/cartera', {
        params: {
          tipoMovimiento: lado,
          idtercero: idTercero,
          desde: '1900-01-01',
          hasta: hastaSaldo
        }
      });

      const index = {};
      (facturas || []).forEach((f) => {
        index[f.id] = {
          numero: f.numero_factura,
          saldo: Number(f.saldo || 0),
          mark: f.terceroNombre || ''
        };
      });
      setFacturasIndex(index);

      // ---------- TIMELINE ----------
      const respTL = await api.get('/api/cartera/timeline', {
        params: {
          tipoMovimiento: lado,
          idtercero: idTercero,
          desde: desdeBusqueda,
          hasta: hastaBusqueda
        }
      });

      const tl = respTL.data;
      const headerSaldo = respTL?.headers?.['x-saldo-anterior'];
      const headerTieneSaldo =
        headerSaldo !== undefined && headerSaldo !== null && String(headerSaldo).trim() !== '';

      const normTL = (Array.isArray(tl) ? tl : []).map((r) => {
        const tipo = String(r.tipoDocumento || r.tipo || '').toUpperCase();
        const facturasRelacionadas = String(r.facturas_relacionadas || '')
          .split(',')
          .map((id) => Number(id))
          .filter(Boolean);

        const amount =
          Number(r.amount ?? (['F', 'ND', 'SI'].includes(tipo) ? (r.valorTotal ?? r.valor) : 0)) ||
          0;

        const credits =
          Number(r.credits ?? (['NC', 'RT'].includes(tipo) ? (r.valorTotal ?? r.valor) : 0)) || 0;

        const payment = Number(r.payment ?? (tipo === 'PG' ? (r.valorTotal ?? r.valor) : 0)) || 0;

        return {
          id: Number(r.idfactura ?? r.id ?? 0),
          fecha: (r.fecha || '').slice(0, 10),
          tipoDocumento: tipo,
          numero: r.numero_factura ?? r.numero ?? '',
          mark: r.mark ?? r.terceroNombre ?? r.terceroNombrePrincipal ?? '',
          obs: r.observaciones ?? r.obs ?? '',
          facturasRelacionadas,
          editable: !!r.editable,
          idbanco: r.idbanco ?? null,
          costo_bancario: r.costo_bancario ?? null,
          numero_comprobante: r.numero_comprobante ?? '',
          amount,
          credits,
          payment
        };
      });

      setTimeline(normTL);
      setRelacionMarcada(null);

      // ---------- SALDO ANTERIOR ----------
      let saldoInicialCalc = 0;

      if (headerTieneSaldo) {
        const n = Number(headerSaldo);
        saldoInicialCalc = Number.isFinite(n) ? n : 0;
      } else {
        // ✅ CLAVE: si desde es 1900-01-01, no existe saldo anterior.
        // Evitamos llamar al backend con 1899-12-31.
        if (desdeBusqueda <= '1900-01-01') {
          saldoInicialCalc = 0;
        } else {
          const hastaPrev = addDaysYmd(desdeBusqueda, -1);

          if (hastaPrev && hastaPrev >= '1900-01-01') {
            const { data: tlPrev } = await api.get('/api/cartera/timeline', {
              params: {
                tipoMovimiento: lado,
                idtercero: idTercero,
                desde: '1900-01-01',
                hasta: hastaPrev
              }
            });

            const normPrev = (Array.isArray(tlPrev) ? tlPrev : []).map((r) => {
              const tipo = String(r.tipoDocumento || r.tipo || '').toUpperCase();

              const amount =
                Number(
                  r.amount ?? (['F', 'ND', 'SI'].includes(tipo) ? (r.valorTotal ?? r.valor) : 0)
                ) || 0;

              const credits =
                Number(
                  r.credits ?? (['NC', 'RT'].includes(tipo) ? (r.valorTotal ?? r.valor) : 0)
                ) || 0;

              const payment =
                Number(r.payment ?? (tipo === 'PG' ? (r.valorTotal ?? r.valor) : 0)) || 0;

              return { tipo, amount, credits, payment };
            });

            const sinPP = normPrev.filter((x) => x.tipo !== 'PP');

            saldoInicialCalc = sinPP.reduce(
              (acc, r) =>
                acc + Number(r.amount || 0) - Number(r.credits || 0) - Number(r.payment || 0),
              0
            );
          }
        }
      }

      setSaldoInicial(saldoInicialCalc);

      // --------- PREPAGOS ----------
      const { data: pps } = await api.get('/api/cartera/prepagos', {
        params: { tipoMovimiento: lado, idtercero: idTercero }
      });

      const norm = (Array.isArray(pps) ? pps : []).map((p) => {
        const tipo = (p.tipoDocumento || p.tipo || 'PP').toString().toUpperCase();

        const monto =
          pickNum(p, [
            'monto',
            'valor',
            'amount',
            'monto_total',
            'valor_total',
            'total',
            'valorTotal',
            'amount_total'
          ]) || 0;

        const aplicado =
          pickNum(p, [
            'aplicado',
            'usado',
            'aplicado_total',
            'valor_aplicado',
            'payment',
            'credits',
            'pagado'
          ]) || 0;

        let disponible =
          pickNum(p, ['saldo', 'disponible', 'restante', 'pendiente', 'por_aplicar']) || 0;

        if (!disponible && (monto || aplicado)) {
          disponible = Math.max(0, monto - aplicado);
        }

        const montoFinal = monto || aplicado + disponible;

        return {
          id: Number(p.id || p.idpago || p.idprepago || p.idmov || 0),
          fecha: (p.fecha || p.fecha_pago || p.created_at || '').slice(0, 10),
          tipo,
          mark: p.mark || p.terceroNombre || '',
          monto: toNum(montoFinal),
          aplicado: toNum(aplicado),
          disponible: toNum(disponible),
          obs: p.obs || p.observaciones || '',
          editable:
            p.editable !== undefined && p.editable !== null
              ? Number(p.editable) === 1
              : toNum(aplicado) === 0
        };
      });

      setPrepagos(norm);
      setHayPrepagos(norm.length > 0);
    } catch (err) {
      console.error('❌ Error al buscar cartera:', err?.response?.data || err);
      alert(
        err?.response?.data?.message || err?.response?.data?.error || '❌ Error al buscar cartera'
      );
    }
  };

  // selección y pagos
  const actualizarValorPago = (idFactura, valor) => {
    setSeleccionados((prev) => ({ ...prev, [idFactura]: valor }));
  };

  const toggleSeleccion = (row) => {
    const idFactura = row.id;
    const saldo = facturasIndex[idFactura]?.saldo || 0;
    setSeleccionados((prev) => {
      const nuevo = { ...prev };
      if (nuevo[idFactura] !== undefined) {
        delete nuevo[idFactura];
      } else {
        nuevo[idFactura] = saldo > 0 ? saldo.toFixed(2) : '';
      }
      return nuevo;
    });
  };

  const facturasSeleccionadas = useMemo(
    () =>
      Object.entries(seleccionados)
        .filter(([, v]) => v !== '' && Number(v) > 0)
        .map(([id, v]) => ({
          idfactura: Number(id),
          valorpago: Number(v),
          numero_factura: facturasIndex[id]?.numero
        })),
    [seleccionados, facturasIndex]
  );

  // estado de cuenta
  const abrirModalEstadoCuenta = (tercero) => {
    if (!desde || !hasta) {
      alert('❗ Debes seleccionar un rango de fechas para ver el estado de cuenta.');
      return;
    }
    setTerceroActual(tercero);
    setRangoFechas({ desde, hasta });
    setMostrarModalEstadoCuenta(true);
  };

  const terceroSeleccionado = terceros.find((t) => String(t.idtercero) === String(idTercero));

  // ---- MOVIMIENTOS visibles (quitamos solo PP) ----
  const rowsMov = useMemo(
    () =>
      (timeline || []).filter((r) => {
        const t = (r.tipoDocumento || r.tipo || '').toString().toUpperCase();
        return t !== 'PP';
      }),
    [timeline]
  );

  // Solo en Proveedores
  const esProveedor = String(tipoMovimiento || '')
    .toUpperCase()
    .startsWith('P');

  // ---- Enriquecer filas con saldos acumulados ----
  const rowsConSaldo = useMemo(() => {
    let running = Number(saldoInicial || 0);

    return rowsMov.map((r) => {
      const tipo = (r.tipoDocumento || r.tipo || '').toUpperCase();
      const amount = Number(r.amount || 0);
      const credits = Number(r.credits || 0);
      const payment = Number(r.payment || 0);

      // ✅ Solo Proveedores: separar factura cliente del mark
      const { facturaCliente, markLimpio } = esProveedor
        ? splitFacturaCliente(r.mark)
        : { facturaCliente: '', markLimpio: r.mark };

      running += amount - credits - payment;

      return {
        ...r,
        // ✅ si es Proveedor, dejamos mark limpio; si es Cliente, queda igual
        mark: markLimpio ?? r.mark,
        __facturaCliente: facturaCliente,

        __tipo: tipo,
        __amount: amount,
        __credits: credits,
        __payment: payment,
        __runningDue: running
      };
    });
  }, [rowsMov, saldoInicial, esProveedor]);

  const totals = rowsConSaldo.reduce(
    (acc, r) => {
      acc.amount += r.__amount;
      acc.credits += r.__credits;
      acc.payment += r.__payment;
      return acc;
    },
    { amount: 0, credits: 0, payment: 0 }
  );

  const amountDueBase = rowsConSaldo.length
    ? rowsConSaldo[rowsConSaldo.length - 1].__runningDue
    : Number(saldoInicial || 0);

  // Las NC ya reducen el balance en la linea del movimiento; solo PP se resta aparte.
  const totalAnticipo = prepagos
    .filter((p) => String(p.tipo || '').toUpperCase() === 'PP')
    .reduce((s, p) => s + Number(p.disponible || 0), 0);

  const balanceFinal = amountDueBase - totalAnticipo;

  const totalMovSel = Object.values(seleccionados).reduce(
    (s, v) => s + (v === '' ? 0 : Number(v || 0)),
    0
  );

  const tieneFacturas = facturasSeleccionadas.length > 0;
  // ModalMovimiento ya no maneja NC:
  const tiposPermitidos = tieneFacturas ? ['PG', 'ND', 'RT'] : ['PP', 'SI'];

  const esPagable = useCallback((tipo) => tipo === 'F' || tipo === 'SI', []);

  // ✅ filas visibles con filtro “Solo pendientes”
  const rowsVisibles = useMemo(() => {
    if (!soloPendientes) return rowsConSaldo;
    return rowsConSaldo.filter(
      (r) => esPagable(r.__tipo) && Number(facturasIndex[r.id]?.saldo || 0) > 0
    );
  }, [rowsConSaldo, soloPendientes, facturasIndex, esPagable]);

  // ✅ Fila visible de saldo anterior (para que el usuario vea con qué arranca el rango)
  const rowSaldoAnterior = useMemo(() => {
    const val = Number(saldoInicial || 0);
    if (!desde || !hasta) return null;

    // ✅ Cuando consultamos desde 1900-01-01 no mostramos 31/12/1899
    if (desde <= '1900-01-01') return null;

    return {
      id: 'saldo-anterior',
      fecha: addDaysYmd(desde, -1) || desde,
      tipoDocumento: 'SA',
      numero: '',
      mark: 'SALDO ANTERIOR',
      obs: `Antes de ${dmy(desde)}`,
      editable: false,
      amount: 0,
      credits: 0,
      payment: 0,

      __facturaCliente: '',
      __tipo: 'SA',
      __amount: 0,
      __credits: 0,
      __payment: 0,
      __runningDue: val,
      __isSaldoAnterior: true
    };
  }, [saldoInicial, desde, hasta, dmy]);

  const rowsTablaMov = useMemo(() => {
    const base = rowsVisibles || [];
    return rowSaldoAnterior ? [rowSaldoAnterior, ...base] : base;
  }, [rowsVisibles, rowSaldoAnterior]);

  const rowsTablaMovFiltradas = useMemo(() => {
    const textoBuscado = normalizarTextoBusqueda(busquedaMovimientos);
    if (!textoBuscado) return rowsTablaMov;

    return rowsTablaMov.filter((row) => {
      const tipo = String(row?.__tipo || row?.tipoDocumento || row?.tipo || '').toUpperCase();
      const numerosFacturasRelacionadas = (row?.facturasRelacionadas || [])
        .map((id) => facturasIndex[id]?.numero)
        .filter(Boolean)
        .join(' ');

      const contenidoBuscable = [
        row?.numero,
        row?.numero_comprobante,
        row?.__facturaCliente,
        numerosFacturasRelacionadas,
        row?.mark,
        tipo,
        ETIQUETAS_TIPO_DOCUMENTO[tipo],
        row?.obs,
        row?.fecha,
        row?.fecha ? dmy(row.fecha) : ''
      ].join(' ');

      return normalizarTextoBusqueda(contenidoBuscable).includes(textoBuscado);
    });
  }, [busquedaMovimientos, rowsTablaMov, facturasIndex, dmy]);
  // edición (usa el mismo modal)
  const rowKey = useCallback(
    (row) =>
      `${String(row?.__tipo || row?.tipoDocumento || row?.tipo || '').toUpperCase()}-${row?.id}`,
    []
  );

  const facturasRelacionadasDe = useCallback(
    (row) => {
      const directas = Array.isArray(row?.facturasRelacionadas)
        ? row.facturasRelacionadas.map((id) => Number(id)).filter(Boolean)
        : [];
      if (directas.length) return directas;
      return esPagable(row?.__tipo) && Number(row?.id) ? [Number(row.id)] : [];
    },
    [esPagable]
  );

  const relacionesPorFactura = useMemo(() => {
    const map = new Map();
    (rowsConSaldo || []).forEach((row) => {
      const key = rowKey(row);
      const ids = Array.isArray(row.facturasRelacionadas)
        ? row.facturasRelacionadas.map((id) => Number(id)).filter(Boolean)
        : [];
      ids.forEach((id) => {
        if (!map.has(id)) map.set(id, []);
        map.get(id).push(key);
      });
    });
    return map;
  }, [rowsConSaldo, rowKey]);

  const puedeMarcarRelacion = useCallback(
    (row) => {
      if (!row || row.__isSaldoAnterior) return false;
      const ids = facturasRelacionadasDe(row);
      if (!ids.length) return false;
      if (!esPagable(row.__tipo)) return true;
      return ids.some((id) => relacionesPorFactura.has(Number(id)));
    },
    [esPagable, facturasRelacionadasDe, relacionesPorFactura]
  );

  const toggleMarcaRelacion = useCallback(
    (row) => {
      if (!puedeMarcarRelacion(row)) return;
      const ids = facturasRelacionadasDe(row);
      const key = rowKey(row);
      setRelacionMarcada((prev) => (prev?.key === key ? null : { key, facturaIds: ids }));
    },
    [facturasRelacionadasDe, puedeMarcarRelacion, rowKey]
  );

  const estiloRelacion = useCallback(
    (row) => {
      if (!relacionMarcada || !puedeMarcarRelacion(row)) return null;
      const ids = facturasRelacionadasDe(row);
      const relacionada = ids.some((id) => relacionMarcada.facturaIds.includes(Number(id)));
      if (!relacionada) return null;

      if (rowKey(row) === relacionMarcada.key) {
        return { background: '#fce7f3', boxShadow: 'inset 4px 0 #f0a6c8' };
      }
      if (esPagable(row.__tipo)) {
        return { background: '#dcfce7', boxShadow: 'inset 4px 0 #86d9a4' };
      }
      return { background: '#e0f2fe', boxShadow: 'inset 4px 0 #8ecae6' };
    },
    [esPagable, facturasRelacionadasDe, puedeMarcarRelacion, relacionMarcada, rowKey]
  );

  const abrirEditarMovimiento = (row) => {
    if (!row.editable) return;

    const tipoDetectado = (row.tipoDocumento || row.tipo || row.tipoDoc || row.tipodocumento || '')
      .toString()
      .toUpperCase();

    if (tipoDetectado === 'NC') {
      setMovimientoParaEditar(null);
      setIdpagoNCEditar(row.id);
      setShowNC(true);
      return;
    }

    const valorBase =
      Number(row.payment || 0) || Number(row.credits || 0) || Number(row.amount || 0) || 0;

    setMovimientoParaEditar({
      id: row.id,
      tipoDocumento: tipoDetectado,
      fecha: (row.fecha || '').slice(0, 10),
      valorTotal: valorBase,
      observaciones: row.obs || '',
      numero_factura: row.numero || '',
      idbanco: row.idbanco ?? null,
      costo_bancario: row.costo_bancario ?? null,
      numero_comprobante: row.numero_comprobante ?? ''
    });

    setShowModal(true);
  };

  const buildDeleteImpactMsg = (impact) => {
    const p = impact?.pago;
    if (!p) return '¿Eliminar este movimiento?';

    const lines = [];
    lines.push('⚠️ VAS A ELIMINAR ESTE MOVIMIENTO:');
    lines.push(`• ID Pago: ${p.idpago}`);
    lines.push(
      `• Tipo: ${String(p.tipoDocumento || '').toUpperCase()} (${String(p.tipoMovimiento || '').toUpperCase()})`
    );
    lines.push(`• Fecha: ${String(p.fecha || '').slice(0, 10)}`);
    lines.push(`• Tercero: ${p.tercero || ''}`);
    lines.push(`• Valor: ${fmt(p.valor || 0)}`);
    if (p.observaciones) lines.push(`• Obs: ${p.observaciones}`);

    const advertencias = Array.isArray(impact?.advertencias) ? impact.advertencias : [];
    if (advertencias.length) {
      lines.push('');
      for (const msg of advertencias) lines.push(`• ${msg}`);
    }

    const facturas = impact?.facturas || [];
    if (String(p.tipoDocumento).toUpperCase() === 'PG' && facturas.some(f => f.idpago_pp)) {
      lines.push('Se devuelve el saldo utilizado a sus anticipos/NC de origen y se liberan las facturas. Los anticipos originales se conservan.');
    }
    if (facturas.length) {
      lines.push('');
      lines.push(`Se eliminarán ${facturas.length} cruce(s) con factura(s):`);
      for (const f of facturas) {
        lines.push(
          `- ${f.numero_factura || ''} | ${String(f.fecha || '').slice(0, 10)} | ${fmt(f.valorpago || 0)}`
        );
      }
    }

    // Solo aplica si es NC/RT con detalle
    const proveedores = impact?.proveedores || [];
    const lineasNc = Number(impact?.lineas_nc || 0);
    const fotos = Number(impact?.fotos || 0);
    if (lineasNc || fotos || proveedores.length) {
      lines.push('');
      lines.push(`Detalle NC: ${lineasNc} línea(s), ${fotos} foto(s).`);
      if (proveedores.length) {
        lines.push('Por proveedor:');
        for (const pr of proveedores) {
          lines.push(
            `- ${pr.proveedor || '(sin nombre)'}: ${pr.lineas} línea(s) | ${fmt(pr.monto || 0)}`
          );
        }
      }
    }

    // ✅ NUEVO: si esta NC creó movimientos espejo en PROVEEDORES, también se borrarán
    const pagosProveedores = impact?.pagos_proveedores || [];
    if (pagosProveedores.length) {
      lines.push('');
      lines.push(`También se eliminarán ${pagosProveedores.length} NC(s) en PROVEEDORES:`);
      for (const pp of pagosProveedores) {
        lines.push(
          `- ${pp.proveedor || 'Prov ' + (pp.proveedor_id ?? '')}: ID ${pp.idpago} | ${fmt(pp.valor || 0)}`
        );
      }
    }

    lines.push('');
    lines.push('✅ ¿Confirmas eliminar TODO lo anterior?');

    return lines.join('\n');
  };

  const confirmarYEliminarPago = async (idpago) => {
    const { data: impact } = await api.get(`/api/cartera/pago/${idpago}/delete-impact`);

    if (impact?.puedeEliminar === false) {
      alert(
        impact?.motivoBloqueo || 'Este prepago/anticipo ya fue utilizado y no se puede eliminar.'
      );
      return false;
    }

    const ok = window.confirm(buildDeleteImpactMsg(impact));
    if (!ok) return false;

    await api.delete(`/api/cartera/pago/${idpago}`);
    return true;
  };
  // ✅ Evita que "Descripción (Mark)" y "Obs" desborden la página
  const COL_DESC = 220; // ajusta a gusto (200-260)
  const COL_OBS = 260; // ajusta a gusto (240-340)

  const tableFixed = { width: '100%', tableLayout: 'fixed' };

  const cellWrap = {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    lineHeight: 1.15,
    verticalAlign: 'top'
  };

  const thDesc = { width: COL_DESC };
  const thObs = { width: COL_OBS };

  const tdDesc = { ...cellWrap, maxWidth: COL_DESC };
  const tdObs = { ...cellWrap, maxWidth: COL_OBS };

  // ✅ Ajustes de ancho para columnas pequeñas (Tipo / Dinero / Acciones)
  const COL_TIPO = 50; // 2-3 letras
  const COL_DATE = 120; // ✅ Fecha más ancha
  const COL_MONEY = 80;
  const COL_DUE = 100; // Amount Due un poco más ancho
  const COL_ACC = 120; // para ver bien botones

  // ✅ NUEVO: controlar "Factura" y "Mov." (lo que señalaste en la imagen)
  const COL_FACTURA = 90;
  const COL_MOV = 100; // ✅ más ancho para el input (Mov.)

  const thDate = { width: COL_DATE, textAlign: 'center' };
  const tdDate = { width: COL_DATE, textAlign: 'center', whiteSpace: 'nowrap' };

  const thTipo = { width: COL_TIPO, textAlign: 'center' };
  const tdTipo = { width: COL_TIPO, textAlign: 'center', whiteSpace: 'nowrap' };

  const thMoney = { width: COL_MONEY, textAlign: 'right' };
  const tdMoney = { width: COL_MONEY, textAlign: 'right', whiteSpace: 'nowrap' };

  const thDue = { width: COL_DUE, textAlign: 'right' };
  const tdDue = { width: COL_DUE, textAlign: 'right', whiteSpace: 'nowrap' };

  const thAcc = { width: COL_ACC, textAlign: 'center' };
  const tdAcc = { width: COL_ACC, textAlign: 'center', whiteSpace: 'nowrap' };

  const thMov = { width: COL_MOV, textAlign: 'center' };
  const tdMov = { width: COL_MOV, textAlign: 'center', whiteSpace: 'nowrap' };

  const thFactura = { width: COL_FACTURA, textAlign: 'center' };
  const tdFactura = { width: COL_FACTURA, textAlign: 'center', whiteSpace: 'nowrap' };

  const accWrap = { display: 'inline-flex', gap: 6, alignItems: 'center' };

  // ✅ Handler del botón
  const pagarPorAntiguedad = async () => {
    if (!idTercero) return alert('Selecciona un tercero.');
    if (!desde || !hasta) return alert('Selecciona desde / hasta y pulsa Buscar.');

    const raw = window.prompt('Valor recibido para pagar por antigüedad (USD):', '');
    if (raw == null) return; // cancel

    const monto = toNum(raw);
    if (!(monto > 0)) return alert('Valor inválido.');

    let reparto;
    try {
      // No depender de filtros visuales ni de un setState pendiente.
      const { data } = await api.get('/api/cartera', {
        params: { tipoMovimiento, idtercero: idTercero, desde: '1900-01-01', hasta }
      });
      const facturas = Array.isArray(data) ? data : [];
      const index = {};
      facturas.forEach(f => {
        index[f.id] = { numero: f.numero_factura, saldo: Number(f.saldo || 0), mark: f.terceroNombre || '' };
      });
      reparto = distribuirPorAntiguedad(facturas, monto);
      setFacturasIndex(index);
      setSeleccionados(reparto.seleccionados);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudieron actualizar los saldos para distribuir el pago.');
      return;
    }
    const { aplicado, restante, totalPendiente } = reparto;

    if (!(aplicado > 0)) {
      setSeleccionados({});
      return alert('No hay facturas pendientes (F/SI) con saldo > 0 para aplicar.');
    }

    if (restante > 0) {
      alert(
        `El monto excede el saldo pendiente.\n` +
          `Pendiente total: ${fmt(totalPendiente)}\n` +
          `Se aplicará: ${fmt(aplicado)}\n` +
          `Sobrante: ${fmt(restante)} (puedes registrarlo como PREPAGO)`
      );
    }

    // Abrimos el mismo modal de movimientos (ya con facturasSeleccionadas llenas)
    setMovimientoParaEditar(null);
    setShowModal(true);
  };

  // ✅ Para colSpans del footer cuando es proveedor (tiene 1 columna extra)
  const shiftProv = esProveedor ? 1 : 0;

  return (
    <div className="cartera-page">
      <h2>📒 Cartera {tipoMovimiento === 'C' ? 'Clientes' : 'Proveedores'}</h2>

      <div className="barra-filtros-cartera">
        <select
          value={tipoMovimiento}
          onChange={(e) => {
            setTipoMovimiento(e.target.value);
            setIdTercero('');
            setBusquedaMovimientos('');
            setTimeline([]);
            setFacturasIndex({});
            setSeleccionados({});
            setRelacionMarcada(null);
            setPrepagos([]);
            setHayPrepagos(false);
            setSaldoInicial(0);
          }}
        >
          <option value="C">Clientes</option>
          <option value="P">Proveedores</option>
        </select>

        <div className="tercero-autocomplete">
          <Select
            inputId="cartera-tercero"
            classNamePrefix="tercero-select"
            value={opcionTerceroSeleccionada}
            options={opcionesTerceros}
            onChange={(opcion) => {
              setIdTercero(opcion?.value || '');
              setBusquedaMovimientos('');
              setSeleccionados({});
              setRelacionMarcada(null);
              setTimeline([]);
              setFacturasIndex({});
              setPrepagos([]);
              setHayPrepagos(false);
              setSaldoInicial(0);
            }}
            placeholder={
              tipoMovimiento === 'C'
                ? 'Buscar cliente principal...'
                : 'Buscar proveedor...'
            }
            noOptionsMessage={() => 'No se encontraron coincidencias'}
            isClearable
            isSearchable
            menuPlacement="auto"
            aria-label={tipoMovimiento === 'C' ? 'Buscar cliente' : 'Buscar proveedor'}
          />
        </div>

        <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
        <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />

        {/* ✅ Toggle “Solo pendientes” con automatización de fechas y búsqueda */}
        <label className="filtro-pendientes">
          <input
            type="checkbox"
            checked={soloPendientes}
            onChange={async (e) => {
              const checked = e.target.checked;
              setSoloPendientes(checked);

              if (checked) {
                const hoy = new Date().toISOString().slice(0, 10);
                const desdePendientes = '1900-01-01';

                setDesde(desdePendientes);
                setHasta(hoy);

                if (idTercero) {
                  await buscar({
                    desde: desdePendientes,
                    hasta: hoy
                  });
                }
              }
            }}
          />
          <span>Pendientes</span>
        </label>

        <button onClick={buscar} disabled={!idTercero || !desde || !hasta}>
          🔍 Buscar
        </button>

        <button
          onClick={() => {
            setMovimientoParaEditar(null);
            setShowModal(true);
          }}
          title={
            tieneFacturas
              ? 'Registrar PG/ND/RT para las facturas marcadas'
              : 'Registrar Prepago o Saldo Inicial'
          }
        >
          {tieneFacturas ? '➕ Registrar Movimiento' : '➕ Prepago / Saldo inicial'}
        </button>

        <button
          onClick={pagarPorAntiguedad}
          disabled={!idTercero || !desde || !hasta}
          title="Ingresa un valor y se aplicará a las facturas más antiguas (FIFO)"
          style={{ marginLeft: 8 }}
        >
          🧾 Pago por antigüedad
        </button>

        {/* Botón independiente para Nota de Crédito */}
        <button
          onClick={() => {
            setIdpagoNCEditar(null);
            setShowNC(true);
          }}
          disabled={!tieneFacturas || tipoMovimiento !== 'C'}
          title={
            !tieneFacturas
              ? 'Marca una o más facturas para aplicar Nota de Crédito'
              : tipoMovimiento !== 'C'
                ? 'La Nota de Crédito se aplica del lado de Clientes'
                : 'Aplicar Nota de Crédito sobre las facturas marcadas'
          }
          style={{ marginLeft: 8 }}
        >
          ➕ Nota de Crédito
        </button>

        {hayPrepagos && (
          <button onClick={() => setShowPrepagos(true)} style={{ marginLeft: 8 }}>
            💠 Aplicar saldos a favor
          </button>
        )}

        <button onClick={() => setMostrarReportes(true)}>Reportes de cartera / PO</button>
        <button onClick={() => setMostrarModalReportesMaster(true)} style={{ marginLeft: 8 }}>
          Reportes master
        </button>

        {terceroSeleccionado && (
          <div className="fila-tercero" style={{ marginTop: 6 }}>
            <button
              onClick={() =>
                abrirModalEstadoCuenta({
                  id: terceroSeleccionado.idtercero,
                  nombre: terceroSeleccionado.nombre,
                  tipo: tipoMovimiento
                })
              }
            >
              📄 Estado de Cuenta
            </button>
          </div>
        )}
      </div>

      {/* Panel de prepagos/saldos iniciales disponibles */}
      {prepagos.length > 0 && (
        <>
          <h3 style={{ marginTop: 16 }}>
            🔹 Saldos a favor y anticipos disponibles
            <small style={{ marginLeft: 8, color: '#7a7a7a' }}>
              ({prepagos.length} {prepagos.length === 1 ? 'registro' : 'registros'})
            </small>
          </h3>

          <div className="cartera-table-scroll cartera-prepagos-scroll">
          <table className="tabla-cartera" style={tableFixed}>
            <thead>
              <tr>
                <th style={{ width: 28 }} />
                <th style={thDate}>Fecha</th>
                <th style={thDesc}>Descripción (Mark)</th>
                <th>Factura</th>
                <th style={thTipo}>Tipo</th>
                <th style={thMoney}>Amount</th>
                <th style={thMoney}>Aplicado</th>
                <th style={thMoney}>Disponible</th>
                <th style={thObs}>Obs</th>
                <th style={thAcc}>Acciones</th>
              </tr>
            </thead>

            <tbody>
              {prepagos.map((p, idx) => {
                const cliente =
                  (p.mark && p.mark !== '-' && p.mark) ||
                  p.subcliente ||
                  p.terceroNombre ||
                  p.terceroNombrePrincipal ||
                  '—';

                const monto = Number(p.monto ?? p.amount ?? 0);
                const disponible = Number(p.disponible ?? 0);
                const aplicado = p.aplicado != null ? Number(p.aplicado) : monto - disponible;

                return (
                  <tr key={`prep-${p.id}-${idx}`}>
                    <td />
                    <td style={tdDate}>{p.fecha ? dmy(p.fecha) : ''}</td>
                    <td style={tdDesc}>{cliente}</td>
                    <td>—</td>
                    <td style={tdTipo}><TipoDocumento tipo={p.tipo} /></td>
                    <td style={tdMoney}>{fmtColumna(monto)}</td>
                    <td style={tdMoney}>{fmtColumna(aplicado)}</td>
                    <td style={{ ...tdMoney, fontWeight: 600 }}>{fmtColumna(disponible)}</td>
                    <td style={tdObs}>{p.obs || ''}</td>
                    <td style={tdAcc}>
                      {p.editable ? (
                        <span style={accWrap}>
                          <button
                            onClick={() => {
                              setMovimientoParaEditar({
                                id: p.id,
                                tipoDocumento: p.tipo,
                                fecha: p.fecha,
                                valorTotal: monto,
                                observaciones: p.obs || ''
                              });
                              setShowModal(true);
                            }}
                            title="Editar"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                const borrado = await confirmarYEliminarPago(p.id);
                                if (borrado) await buscar();
                              } catch (err) {
                                console.error(err);
                                alert('❌ Error al eliminar movimiento');
                              }
                            }}
                            title="Eliminar"
                          >
                            🗑
                          </button>
                        </span>
                      ) : (
                        <span style={{ color: '#aaa' }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>

            <tfoot>
              {(() => {
                const t = prepagos.reduce(
                  (acc, x) => {
                    const monto = Number(x.monto ?? x.amount ?? 0);
                    const disponible = Number(x.disponible ?? 0);
                    const aplicado = x.aplicado != null ? Number(x.aplicado) : monto - disponible;
                    acc.monto += monto;
                    acc.aplicado += aplicado;
                    acc.disponible += disponible;
                    return acc;
                  },
                  { monto: 0, aplicado: 0, disponible: 0 }
                );

                return (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'right', fontWeight: 'bold' }}>
                      TOTALS
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{fmtColumna(t.monto)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{fmtColumna(t.aplicado)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{fmtColumna(t.disponible)}</td>
                    <td colSpan={2} />
                  </tr>
                );
              })()}
            </tfoot>
          </table>
          </div>
        </>
      )}

      <div className="cabecera-movimientos">
        <h3>Movimientos</h3>

        <div className="saldo-anterior-movimientos">
          Saldo anterior al {dmy(desde)}: <strong>{fmt(saldoInicial)}</strong>
        </div>

        <div className="buscador-movimientos">
          <label htmlFor="buscar-movimientos">Buscar movimientos</label>
          <input
            id="buscar-movimientos"
            type="search"
            value={busquedaMovimientos}
            onChange={(e) => setBusquedaMovimientos(e.target.value)}
            placeholder="Factura, mark, tipo, observación, fecha..."
            autoComplete="off"
          />
          {busquedaMovimientos.trim() && (
            <span className="resultado-busqueda-movimientos">
              {rowsTablaMovFiltradas.length} de {rowsTablaMov.length}
            </span>
          )}
        </div>
      </div>

      {/* ✅ TABLA MOVIMIENTOS: aquí estaba tu problema (no aplicabas thDate/tdDate y "Mov." no tenía ancho) */}
      <div ref={gridRef} className="cartera-table-scroll cartera-movimientos-scroll" role="region" aria-label="Movimientos de cartera" tabIndex={0}>
      <table className="tabla-cartera" style={tableFixed}>
        {/* ✅ Colgroup fuerza anchos aunque el CSS intente repartir */}
        <colgroup>
          <col style={{ width: 28 }} />
          <col style={{ width: COL_DATE }} /> {/* Fecha */}
          {esProveedor && <col style={{ width: 130 }} />} {/* Factura cliente */}
          <col style={{ width: COL_DESC }} /> {/* Mark */}
          <col style={{ width: COL_FACTURA }} /> {/* Factura */}
          <col style={{ width: COL_TIPO }} /> {/* Tipo */}
          <col style={{ width: COL_MONEY }} /> {/* Amount */}
          <col style={{ width: COL_MONEY }} /> {/* Credits */}
          <col style={{ width: COL_MONEY }} /> {/* Payment */}
          <col style={{ width: COL_DUE }} /> {/* Amount Due */}
          <col style={{ width: COL_MOV }} /> {/* Mov. */}
          <col style={{ width: COL_OBS }} /> {/* Obs */}
          <col style={{ width: COL_ACC }} /> {/* Acciones */}
        </colgroup>

        <thead>
          <tr>
            <th style={{ width: 28 }} />
            <th style={thDate}>Fecha</th>
            {esProveedor && <th style={{ width: 130, textAlign: 'center' }}>Factura cliente</th>}
            <th style={thDesc}>Descripción (Mark)</th>
            <th style={thFactura}>Factura</th>
            <th style={thTipo}>Tipo</th>
            <th style={thMoney}>Amount</th>
            <th style={thMoney}>Credits</th>
            <th style={thMoney}>Payment</th>
            <th style={thDue}>Amount Due</th>
            <th style={thMov}>Mov.</th>
            <th style={thObs}>Obs</th>
            <th style={thAcc}>Acciones</th>
          </tr>
        </thead>

        <tbody>
          {rowsTablaMovFiltradas.map((row, idx) => {
            const tipoReal = row.__tipo;
            const esItemPagable = esPagable(tipoReal);
            const saldoFactura = esItemPagable ? Number(facturasIndex[row.id]?.saldo || 0) : 0;
            const puedePagar = esItemPagable && saldoFactura > 0;
            const seleccionado = esItemPagable && seleccionados[row.id] !== undefined;
            const puedeMarcar = puedeMarcarRelacion(row);
            const estiloMarcado = estiloRelacion(row);
            const filaMarcada = Boolean(estiloMarcado);
            const styleFila = {
              ...(row.__isSaldoAnterior ? { background: '#f6f7f9', fontWeight: 'bold' } : {}),
              ...(estiloMarcado || {}),
              ...(puedeMarcar ? { cursor: 'pointer' } : {})
            };

            return (
              <tr
                key={`${tipoReal}-${row.id}-${idx}`}
                style={Object.keys(styleFila).length ? styleFila : undefined}
                onClick={() => {
                  if (puedeMarcar) toggleMarcaRelacion(row);
                }}
              >
                <td onClick={(e) => e.stopPropagation()}>
                  {puedePagar && (
                    <input
                      type="checkbox"
                      checked={seleccionado}
                      onChange={() => toggleSeleccion(row)}
                    />
                  )}
                  {!puedePagar && puedeMarcar && (
                    <input
                      type="checkbox"
                      checked={filaMarcada}
                      onChange={() => toggleMarcaRelacion(row)}
                      title="Marcar relación con factura"
                    />
                  )}
                </td>

                {/* ✅ ahora sí: fecha con ancho */}
                <td style={tdDate}>{row.fecha ? dmy(row.fecha) : ''}</td>

                {esProveedor && (
                  <td style={{ width: 130, textAlign: 'center', whiteSpace: 'nowrap' }}>
                    {row.__facturaCliente || '—'}
                  </td>
                )}

                <td style={tdDesc}>{row.mark || '-'}</td>

                {/* ✅ Factura con ancho fijo */}
                <td style={tdFactura}>
                  {!esProveedor && tipoReal === 'F' && row.numero ? (
                    <span role="button" tabIndex={0}
                      title="Doble clic para consultar los PO de esta factura"
                      aria-label={`Ver PO de factura ${row.numero}`}
                      style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                      onDoubleClick={() => setFacturaPO({ id: row.id, numero: row.numero })}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFacturaPO({ id: row.id, numero: row.numero }); } }}
                    >{row.numero}</span>
                  ) : row.numero || ''}
                </td>

                <td style={tdTipo}><TipoDocumento tipo={tipoReal} /></td>
                <td style={tdMoney}>{fmtColumna(row.__amount)}</td>
                <td style={tdMoney}>{fmtColumna(row.__credits)}</td>
                <td style={tdMoney}>{fmtColumna(row.__payment)}</td>
                <td style={tdDue}>{fmtColumna(row.__runningDue)}</td>

                {/* ✅ Mov. con ancho fijo y el input ocupa todo */}
                <td style={tdMov} onClick={(e) => e.stopPropagation()}>
                  {seleccionado ? (
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max={saldoFactura}
                      value={seleccionados[row.id] ?? ''}
                      placeholder="0.00"
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '') {
                          actualizarValorPago(row.id, '');
                          return;
                        }
                        const val = Math.min(parseFloat(raw) || 0, saldoFactura);
                        actualizarValorPago(row.id, val.toString());
                      }}
                      style={{ width: '100%', textAlign: 'right' }}
                      title={`Saldo: ${fmt(saldoFactura)}`}
                    />
                  ) : (
                    '—'
                  )}
                </td>

                <td style={tdObs}>{row.obs || ''}</td>

                <td style={tdAcc} onClick={(e) => e.stopPropagation()}>
                  {row.editable ? (
                    <span style={accWrap}>
                      <button onClick={() => abrirEditarMovimiento(row)} title="Editar">
                        ✏️
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            // SI se elimina de factura_consolidada (tu delete viejo)
                            if (tipoReal === 'SI') {
                              const ok = window.confirm(
                                '⚠️ Vas a eliminar este Saldo Inicial (SI). ¿Confirmas?'
                              );
                              if (!ok) return;
                              await api.delete(`/api/cartera/${row.id}`);
                              await buscar();
                              return;
                            }

                            // PG/PP/NC/RT/ND (tabla pagos): preview -> delete
                            const borrado = await confirmarYEliminarPago(row.id);
                            if (borrado) await buscar();
                          } catch (err) {
                            console.error(err);
                            alert(err.response?.data?.error || '❌ Error al eliminar movimiento');
                          }
                        }}
                        title="Eliminar"
                      >
                        🗑
                      </button>
                    </span>
                  ) : (
                    <span style={{ color: '#aaa' }}>—</span>
                  )}
                </td>
              </tr>
            );
          })}

          {rowsTablaMovFiltradas.length === 0 && (
            <tr>
              <td
                colSpan={esProveedor ? 13 : 12}
                style={{ textAlign: 'center', color: '#666', padding: 12 }}
              >
                {busquedaMovimientos.trim()
                  ? '(no hay movimientos que coincidan con la búsqueda)'
                  : soloPendientes
                    ? '(sin facturas pendientes con saldo)'
                    : '(sin movimientos en el rango)'}
              </td>
            </tr>
          )}
        </tbody>

        <tfoot>
          <tr>
            <td colSpan={esProveedor ? 6 : 5} style={{ textAlign: 'right', fontWeight: 'bold' }}>
              TOTALS
            </td>

            <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{fmtColumna(totals.amount)}</td>
            <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{fmtColumna(totals.credits)}</td>
            <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{fmtColumna(totals.payment)}</td>
            <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
              {fmtColumna(
                rowsConSaldo.length
                  ? rowsConSaldo[rowsConSaldo.length - 1].__runningDue
                  : Number(saldoInicial || 0)
              )}
            </td>
            <td colSpan={3} />
          </tr>

          {totalAnticipo > 0 && (
            <tr>
              <td colSpan={8 + shiftProv} style={{ textAlign: 'right', fontWeight: 'bold' }}>
                Anticipo
              </td>
              <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{fmtColumna(totalAnticipo)}</td>
              <td colSpan={3} />
            </tr>
          )}

          <tr>
            <td colSpan={8 + shiftProv} style={{ textAlign: 'right', fontWeight: 'bold' }}>
              Balance
            </td>
            <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{fmtColumna(balanceFinal)}</td>
            <td colSpan={3} />
          </tr>

          <tr>
            <td colSpan={9 + shiftProv} style={{ textAlign: 'right', fontWeight: 'bold' }}>
              Mov. seleccionado
            </td>
            <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{fmtColumna(totalMovSel)}</td>
            <td colSpan={2} />
          </tr>
        </tfoot>
      </table>
      </div>

      {showModal && (
        <ModalMovimiento
          show={showModal}
          onClose={() => {
            setShowModal(false);
            setMovimientoParaEditar(null);
            setSeleccionados({});
            buscar();
          }}
          tipoMovimiento={tipoMovimiento}
          idSeleccionado={idTercero}
          movimientoParaEditar={movimientoParaEditar}
          facturasSeleccionadas={facturasSeleccionadas}
          tiposPermitidos={tiposPermitidos}
        />
      )}

      {showNC && (
        <ModalNotaCredito
          show={showNC}
          onClose={() => {
            setShowNC(false);
            setIdpagoNCEditar(null);
            setSeleccionados({});
            buscar();
          }}
          tipoMovimiento={tipoMovimiento} // usualmente 'C'
          idSeleccionado={idTercero}
          facturasSeleccionadas={facturasSeleccionadas}
          idpagoEditar={idpagoNCEditar}
        />
      )}

      {showPrepagos && (
        <ModalAplicarPrepagos
          show={showPrepagos}
          onClose={() => {
            setShowPrepagos(false);
            buscar();
          }}
          tipoMovimiento={tipoMovimiento}
          idTercero={idTercero}
          desde={desde}
          hasta={hasta}
        />
      )}

      {mostrarReportes && <ModalReportesCartera onClose={() => setMostrarReportes(false)} />}
      {facturaPO && <ModalDetallePO factura={facturaPO} onClose={() => setFacturaPO(null)} />}
      {mostrarModalReportesMaster && (
        <ModalReportesMasterCartera
          show={mostrarModalReportesMaster}
          onClose={() => setMostrarModalReportesMaster(false)}
        />
      )}

      {mostrarModalEstadoCuenta && terceroActual && (
        <ModalEstadoCuenta
          show={mostrarModalEstadoCuenta}
          onClose={() => setMostrarModalEstadoCuenta(false)}
          tercero={terceroActual}
          desde={rangoFechas.desde}
          hasta={rangoFechas.hasta}
        />
      )}
    </div>
  );
}

export default CarteraPage;
